/**
 * Component: sa-bundle-sale
 * Description: Bundle sale component that appends selected variants to cart form submission
 */
(function () {
  if (customElements.get('sa-bundle-sale')) return;

  class SaBundleSale extends HTMLElement {
    constructor() {
      super();
      this.onFormData = this.onFormData.bind(this);
      this.onQtyClick = this.onQtyClick.bind(this);
      this.updateCart = this.updateCart.bind(this);
      this.cart = null;
    }

    connectedCallback() {
      const formId = this.getAttribute('data-form-id');
      const scope = this.closest('product-rerender') || document;
      
      this.forms = [];
      if (formId) {
        // Main form
        const mainForm = scope.querySelector(`#${CSS.escape(formId)}`);
        if (mainForm) this.forms.push(mainForm);
        
        // Sticky add to cart form (usually appended with -quick-add)
        const stickyFormId = `${formId}-quick-add`;
        const stickyForm = document.querySelector(`#${CSS.escape(stickyFormId)}`);
        if (stickyForm) this.forms.push(stickyForm);
      }

      this.forms.forEach(form => {
        form.addEventListener('formdata', this.onFormData);
      });

      // Bind qty controls within scope
      this.querySelectorAll('.sa-bundle-sale__qty-btn').forEach((btn) => {
        btn.addEventListener('click', this.onQtyClick);
      });

      // Track cart state for inventory checks
      document.addEventListener('cart:change', this.updateCart);
      this.fetchCart();
    }

    disconnectedCallback() {
      if (this.forms) {
        this.forms.forEach(form => {
          form.removeEventListener('formdata', this.onFormData);
        });
      }

      this.querySelectorAll('.sa-bundle-sale__qty-btn').forEach((btn) => {
        btn.removeEventListener('click', this.onQtyClick);
      });

      document.removeEventListener('cart:change', this.updateCart);
    }

    fetchCart() {
      fetch(window.Shopify.routes.root + 'cart.js')
        .then(res => res.json())
        .then(cart => {
          this.cart = cart;
        })
        .catch(console.error);
    }

    updateCart(evt) {
      if (evt.detail && evt.detail.cart) {
        this.cart = evt.detail.cart;
      } else {
        this.fetchCart();
      }
    }

    onQtyClick(e) {
      const btn = e.currentTarget;
      const wrapper = btn.closest('.sa-bundle-sale__qty');
      if (!wrapper) return;
      const input = wrapper.querySelector('.sa-bundle-sale__qty-input');
      if (!input) return;
      const action = btn.getAttribute('data-action');
      const current = parseInt(input.value || '1', 10) || 1;
      input.value = action === 'increase' ? current + 1 : Math.max(1, current - 1);
    }

    onFormData(ev) {
      const fd = ev.formData;

      // Read main product, then switch to items[] format
      const mainId = fd.get('id');
      const mainQty = parseInt(fd.get('quantity') || '1', 10) || 1;

      if (mainId) {
        fd.delete('id');
        fd.delete('quantity');
      }

      // Collect IDs to generate a deterministic bundle ID
      const selectedIds = [];
      // To ensure that the same free gift is merged in the cart regardless of the main product variant,
      // we DO NOT include the main product ID in the bundle hash.
      // The bundle ID should strictly represent the combination of BUNDLE ITEMS selected.
      
      this.querySelectorAll('.sa-bundle-sale__item').forEach((row) => {
        // Fix: Robust ID generation
        // The bundle ID must be deterministic based on the CONFIGURATION, not the STOCK availability.
        // If a free item is part of the bundle, it contributes to the ID even if it is currently OOS.
        // This ensures that when stock fluctuates (e.g., item B goes OOS), the Main Product's identity 
        // (which includes _bundle_id) remains consistent, preventing split line items in the cart.
        
        const isFree = row.getAttribute('data-is-free') === 'true';
        const id = row.getAttribute('data-variant-id');

        if (!id) return;

        if (isFree) {
          // Free items are ALWAYS part of the bundle definition.
          // We force quantity '1' in the hash because free items are implicitly 1-per-main-product.
          // This avoids any instability from hidden inputs or availability checks.
          selectedIds.push(`${id}-1`);
        } else {
          // Paid items are optional and user-selectable.
          const checkbox = row.querySelector('.sa-bundle-sale__checkbox-input');
          if (checkbox && checkbox.checked) {
            const qtyEl = row.querySelector('.sa-bundle-sale__qty-input');
            const qty = parseInt(qtyEl && qtyEl.value ? qtyEl.value : '1', 10) || 1;
            selectedIds.push(`${id}-${qty}`);
          }
        }
      });

      // Simple hash to ensure the bundle ID is the same for the same combination of items
      // v3: Updated to force cache bust and ensure stability
      const bundleId = `bundle_v3_${selectedIds.sort().join('_')}`;

      let index = 0;
      if (mainId) {
        fd.append(`items[${index}][id]`, mainId);
        fd.append(`items[${index}][quantity]`, String(mainQty));
        fd.append(`items[${index}][properties][_bundle_id]`, bundleId);
        fd.append(`items[${index}][properties][_is_main_product]`, 'true');
        index += 1;
      }

      // Append selected bundle items
      this.querySelectorAll('.sa-bundle-sale__item').forEach((row) => {
        const available = row.getAttribute('data-available') === 'true';
        const checkbox = row.querySelector('.sa-bundle-sale__checkbox-input');
        if (!available || !checkbox || !checkbox.checked) return;

        const id = row.getAttribute('data-variant-id');
        const qtyEl = row.querySelector('.sa-bundle-sale__qty-input');
        let qty = parseInt(qtyEl && qtyEl.value ? qtyEl.value : '1', 10) || 1;
        if (!id) return;

        // Only mark as free product if it is actually free
        const isFree = row.getAttribute('data-is-free') === 'true';
        
        // If it is a free product, the quantity should be multiplied by the main product quantity
        // Also store the base quantity (per main item) for cart sync logic
        const baseQty = qty;

        if (isFree) {
          qty = qty * mainQty;
        }

        // --- Inventory Check Logic ---
        // Calculate max allowed quantity based on inventory and cart state
        let allowedQty = qty;
        
        const inventoryPolicy = row.getAttribute('data-inventory-policy'); // 'deny' or 'continue'
        const inventoryQuantityStr = row.getAttribute('data-inventory-quantity');
        
        // Only restrict if policy is 'deny' and inventory data is available
        if (inventoryPolicy === 'deny' && inventoryQuantityStr !== null && inventoryQuantityStr !== '') {
          const maxStock = parseInt(inventoryQuantityStr, 10);
          
          // Check how many are already in cart
          let inCartQty = 0;
          if (this.cart && this.cart.items) {
            // Find item by variant ID (id is string from attribute, cart item.id is number usually)
            const cartItem = this.cart.items.find(item => String(item.id) === String(id));
            if (cartItem) {
              inCartQty = cartItem.quantity;
            }
          }
          
          const remainingStock = Math.max(0, maxStock - inCartQty);
          allowedQty = Math.min(qty, remainingStock);
        }

        // If no stock allowed, skip this item entirely (so main product can still be added)
        // NOTE: Even if skipped here, the Main Product's _bundle_id MUST remain the same (handled above)
        if (allowedQty <= 0) return;
        // -----------------------------

        fd.append(`items[${index}][id]`, id);
        fd.append(`items[${index}][quantity]`, String(allowedQty));
        fd.append(`items[${index}][properties][_bundle_id]`, bundleId);
        
        if (isFree) {
          fd.append(`items[${index}][properties][_is_free_product]`, 'true');
          fd.append(`items[${index}][properties][_gift_base_qty]`, String(baseQty));
        } else {
          // Optional: Mark as bundle item but not free
        }
        
        index += 1;
      });
    }
  }

  customElements.define('sa-bundle-sale', SaBundleSale);
})();

